import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { MatchTicket } from '../components/MatchTicket';
import { track } from '../lib/analytics';
import { api } from '../lib/api';
import { useUser } from '../lib/auth';
import { getTelegram } from '../lib/telegram';

const CONFETTI_KEY = 'punditxi:bracket_confetti_shown';

export function BracketConfirmed() {
  const user = useUser();
  const bracket = useQuery({ queryKey: ['bracket', 'me'], queryFn: () => api.bracketMe() });
  const groups = useQuery({ queryKey: ['wc', 'groups'], queryFn: () => api.wcGroups() });

  const shownRef = useRef<boolean>(false);
  useEffect(() => {
    if (shownRef.current) return;
    const seen = sessionStorage.getItem(CONFETTI_KEY);
    if (!seen) {
      sessionStorage.setItem(CONFETTI_KEY, '1');
      shownRef.current = true;
    } else {
      shownRef.current = true;
    }
  }, []);

  function share() {
    track('share_clicked', { surface: 'bracket' });
    const tg = getTelegram();
    const text = `I locked my PunditXI World Cup bracket. Beat me at @PunditXIBot ⚽️🏆`;
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent('https://t.me/PunditXIBot')}&text=${encodeURIComponent(text)}`;
    if (tg?.openTelegramLink) tg.openTelegramLink(shareUrl);
    else window.open(shareUrl, '_blank');
  }

  if (!bracket.data?.entry) {
    return (
      <div className="mx-auto max-w-md px-4 pt-10">
        <Card>
          <div className="text-text-dim">No bracket entry yet.</div>
          <Link to="/bracket" className="font-display-alt text-xs uppercase tracking-widest text-trophy-gold mt-2 inline-block">
            Buy bracket premium →
          </Link>
        </Card>
      </div>
    );
  }

  const entry = bracket.data.entry;
  const champion = groups.data?.groups
    .flatMap((g) => g.teams)
    .find((t) => t.teamId === entry.championTeamId);

  return (
    <div className="mx-auto max-w-md px-4 pt-4 pb-24 space-y-4">
      <ConfettiBurst show={!shownRef.current} />

      <header className="text-center">
        <div className="font-display-alt text-xs uppercase tracking-widest text-trophy-gold">
          Bracket locked
        </div>
        <h1 className="font-display text-3xl uppercase tracking-wide mt-1">You're in.</h1>
      </header>

      <MatchTicket entryNumber={String(entry.id).padStart(5, '0')} title="PunditXI · World Cup 2026">
        <Row label="Predicted champion" value={champion?.name ?? 'Not picked'} />
        <Row label="Stars paid" value={`${entry.starsPaid} ⭐`} />
        <Row label="Bracket points" value={String(entry.totalPoints)} />
        <Row
          label="Locked at"
          value={
            entry.lockedAt
              ? new Date(entry.lockedAt * 1000).toISOString().slice(0, 16).replace('T', ' ') + ' UTC'
              : 'Locks at WC kickoff'
          }
        />
        <Row label="Holder" value={`${user.data?.firstName ?? '—'}`} />
      </MatchTicket>

      <Button variant="gold" fullWidth onClick={share}>
        Share to chat
      </Button>
      <Link to="/" className="block text-center font-display-alt text-xs uppercase tracking-widest text-text-dim pt-2">
        Back home →
      </Link>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="font-display-alt text-[10px] uppercase tracking-widest opacity-80">{label}</span>
      <span className="font-chunky text-sm text-right">{value}</span>
    </div>
  );
}

function ConfettiBurst({ show }: { show: boolean }) {
  if (!show) return null;
  // Lightweight CSS-only confetti — 24 chunks animating outward
  const chunks = Array.from({ length: 24 }, (_, i) => i);
  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden">
      {chunks.map((i) => {
        const angle = (i / chunks.length) * 360;
        const distance = 240 + Math.random() * 100;
        const x = Math.cos((angle * Math.PI) / 180) * distance;
        const y = Math.sin((angle * Math.PI) / 180) * distance;
        const colors = ['#F5C842', '#E8A82F', '#DC2632', '#16A34A', '#C8B6FF'];
        const color = colors[i % colors.length];
        return (
          <motion.span
            key={i}
            initial={{ x: '50vw', y: '40vh', opacity: 1, scale: 0.4, rotate: 0 }}
            animate={{ x: `calc(50vw + ${x}px)`, y: `calc(40vh + ${y}px)`, opacity: 0, scale: 1, rotate: 360 }}
            transition={{ duration: 1.4, ease: 'easeOut' }}
            className="absolute h-2.5 w-2.5 rounded-sm"
            style={{ background: color }}
          />
        );
      })}
    </div>
  );
}
